import { useState } from 'react';
import { Calendar, type CalendarProps } from './calendar';
import { AdaptiveOverlay, useSheetPlacement } from './adaptive-overlay';
import { KitButton } from './button';
import type { FieldControlProps } from './form-field';
import {
  calendarDate,
  calendarMonth,
  dateAvailable,
  formatCalendarInput,
  parseCalendarInput,
} from './calendar-date.mjs';

export interface DatePickerCopy {
  title: string;
  choose: string;
  close: string;
  previous: string;
  next: string;
  empty: string;
}
type DateOptions = Pick<
  CalendarProps,
  'locale' | 'firstWeekday' | 'today' | 'min' | 'max' | 'readOnly'
> & {
  copy: DatePickerCopy;
};
export type DatePickerProps = FieldControlProps & DateOptions;

export function DatePicker(props: DatePickerProps) {
  const { id, value, onValueChange, onBlur, locale, today, copy, readOnly, disabled } = props;
  const [open, setOpen] = useState(false);
  const selected = calendarDate(value) ? (value as string) : null;
  const [month, setMonth] = useState((selected ?? today).slice(0, 7));
  const text = selected
    ? formatCalendarInput(selected, locale)
    : typeof value === 'string'
      ? value
      : '';
  const choose = (date: string) => {
    if (disabled || readOnly || !dateAvailable(date, props)) return;
    onValueChange(date);
    setOpen(false);
    onBlur();
  };
  return (
    <span
      className="pc-field pc-field-md"
      data-kind="date"
      data-state={disabled ? 'disabled' : readOnly ? 'readonly' : undefined}
    >
      <input
        id={id}
        className="pc-field-input"
        type="text"
        value={text}
        disabled={disabled}
        readOnly={readOnly}
        aria-invalid={props['aria-invalid']}
        aria-describedby={props['aria-describedby']}
        onBlur={onBlur}
        onChange={(event) => {
          if (!disabled && !readOnly)
            onValueChange(
              parseCalendarInput(event.currentTarget.value, locale) ?? event.currentTarget.value,
            );
        }}
      />
      <AdaptiveOverlay
        open={open}
        disabled={disabled || readOnly}
        onOpenChange={(visible) => {
          if (visible) setMonth((selected ?? today).slice(0, 7));
          setOpen(visible);
        }}
        title={copy.title}
        closeLabel={copy.close}
        trigger={
          <KitButton variant="quiet" disabled={disabled || readOnly} aria-label={copy.choose}>
            {copy.choose}
          </KitButton>
        }
      >
        <Calendar
          {...props}
          id={`${id}-calendar`}
          month={month}
          onMonthChange={setMonth}
          value={selected}
          onSelect={choose}
        />
      </AdaptiveOverlay>
    </span>
  );
}

export interface DateRange {
  start: string | null;
  end: string | null;
}
export interface DateRangePickerProps extends DateOptions {
  id: string;
  value: DateRange;
  onValueChange(value: DateRange): void;
  onBlur?(): void;
  disabled?: boolean;
  'aria-invalid'?: boolean;
  'aria-describedby'?: string;
  presets?: Array<{ id: string; label: string; value: DateRange }>;
}

function rangeText(value: DateRange, locale: string, empty: string) {
  if (!value.start) return empty;
  const start = formatCalendarInput(value.start, locale);
  return value.end ? `${start} – ${formatCalendarInput(value.end, locale)}` : start;
}

export function DateRangePicker(props: DateRangePickerProps) {
  const { id, value, today, locale, copy, disabled, readOnly, presets } = props;
  const [open, setOpen] = useState(false);
  const [month, setMonth] = useState((value.start ?? today).slice(0, 7));
  const sheet = useSheetPlacement();
  const choose = (next: DateRange) => {
    if (disabled || readOnly) return;
    if (
      !next.start ||
      !dateAvailable(next.start, props) ||
      (next.end && (!dateAvailable(next.end, props) || next.end < next.start))
    )
      return;
    props.onValueChange(next);
    if (next.end) {
      setOpen(false);
      props.onBlur?.();
    }
  };
  const onSelect = (date: string) =>
    choose(
      value.start && !value.end && date >= value.start
        ? { start: value.start, end: date }
        : { start: date, end: null },
    );
  return (
    <span
      className="pc-field pc-field-md"
      data-kind="date-range"
      data-state={disabled ? 'disabled' : readOnly ? 'readonly' : undefined}
    >
      <AdaptiveOverlay
        open={open}
        disabled={disabled || readOnly}
        onOpenChange={(visible) => {
          if (visible) setMonth((value.start ?? today).slice(0, 7));
          setOpen(visible);
        }}
        title={copy.title}
        closeLabel={copy.close}
        trigger={
          <button
            id={id}
            type="button"
            className="pc-field-input pc-field-menu-trigger"
            disabled={disabled || readOnly}
            aria-invalid={props['aria-invalid']}
            aria-describedby={props['aria-describedby']}
          >
            {rangeText(value, locale, copy.empty)}
          </button>
        }
      >
        {presets && (
          <div className="pc-cal-presets">
            {presets.map((preset) => (
              <KitButton
                key={preset.id}
                variant="quiet"
                disabled={
                  disabled ||
                  readOnly ||
                  !preset.value.start ||
                  !dateAvailable(preset.value.start, props) ||
                  Boolean(
                    preset.value.end &&
                    (!dateAvailable(preset.value.end, props) ||
                      preset.value.end < preset.value.start),
                  )
                }
                onClick={() => choose(preset.value)}
              >
                {preset.label}
              </KitButton>
            ))}
          </div>
        )}
        <Calendar
          {...props}
          id={`${id}-start-calendar`}
          month={month}
          onMonthChange={setMonth}
          range={value}
          value={undefined}
          onSelect={onSelect}
        />
        {!sheet && (
          <Calendar
            {...props}
            id={`${id}-end-calendar`}
            month={calendarMonth(month, 1)}
            onMonthChange={(next) => setMonth(calendarMonth(next, -1))}
            range={value}
            value={undefined}
            onSelect={onSelect}
          />
        )}
      </AdaptiveOverlay>
    </span>
  );
}
